import { z } from 'zod';
import { centAmount, isoDate } from './posting.js';
/**
 * Verträge der Übernahme aus einem **Vorsystem** — einer fremden
 * Buchhaltungssoftware, aus der ein Betrieb seine Buchhaltung selbst
 * herüberholt (erster Fall: Lexware Office).
 *
 * Grundlage: `docs/superpowers/specs/2026-09-29-lexware-migration-design.md`,
 * Abschnitte 5.3, 7 und 15; Schema in den Migrationen `0250` bis `0252`.
 *
 * **Was hier steht und was nicht.** Die Quelle, die Befundform und die
 * Steuerarten der Schnittstelle — also das, was Dienst, Läufer und (ab
 * Stufe B) Assistent gemeinsam lesen. Die Befundarten selbst sind **Daten**
 * (`vorsystem_befundart`, `0251`) und keine Aufzählung: Eine neue Art ist ein
 * `INSERT`, und ein Vertrag, der sie aufzählte, wäre eine zweite Liste, die
 * beim nächsten `INSERT` still veraltete.
 */
/**
 * Die bekannten Vorsysteme, als Wert von `source_system`.
 *
 * **Spiegel von `ist_vorsystem_quelle()`** (Migration `0251`): Die Datenbank
 * zieht mit dieser Funktion jede Grenze der Ära — Belegzeile, Stammdaten,
 * Eingangsrechnungen, Teilindizes. Eine Quelle, die hier steht und dort nicht,
 * bekäme keine einzige der Bedingungen; eine, die dort steht und hier nicht,
 * könnte kein Dienst schreiben. Beide Listen wachsen im selben Commit.
 */
export const VORSYSTEM_QUELLEN = ['lexware-office'];
/** Eine Vorsystem-Quelle. */
export const vorsystemQuelleSchema = z.enum(VORSYSTEM_QUELLEN);
/** Die Quelle Lexware Office — `document.source_system` jedes von dort übernommenen Belegs. */
export const VORSYSTEM_QUELLE_LEXWARE = 'lexware-office';
/**
 * Die Form einer Befundart (`vorsystem_befundart.art`), wortgleich mit der
 * Bedingung `vorsystem_befundart_art_form`.
 */
export const vorsystemBefundartSchema = z.string().regex(/^[a-z][a-z0-9_]{2,60}$/);
/**
 * Ein Befund, wie der Läufer ihn ausgibt (`D-7`, Spec 5.3).
 *
 * **`handlung` ist Pflicht** (`E-1571`): Ein Befund, der dem Kunden nicht
 * sagt, was er tun soll, ist beim Kunden ein Supportfall. `belegnummer` und
 * `belegdatum` nennen den Beleg im Vorsystem, damit er dort auffindbar ist;
 * `beschreibung` nennt die **Regel**, nie einen Klartextwert aus den Rohdaten.
 */
export const vorsystemBefundSchema = z
    .object({
    art: vorsystemBefundartSchema,
    blockierend: z.boolean(),
    beschreibung: z.string().min(1),
    handlung: z.string().min(1),
    quelle: z.string().regex(/^[a-z][a-z0-9-]{1,60}$/),
    fremdschluessel: z.string().min(1),
    feld: z.string().min(1).nullable(),
    belegnummer: z.string().min(1).nullable(),
    belegdatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
})
    .strict();
/**
 * Die Steuerarten der Verkaufsbelege von Lexware Office, wie die öffentliche
 * Dokumentation der Schnittstelle sie nennt (Spec 2.1, [Q1]).
 *
 * Ihre Abbildung auf `tax_scenario` ist **Daten** (`vorsystem_steuerart`,
 * `0251`, `D-23`) und steht nicht hier. Die Kleinunternehmer-Lesart von
 * `vatfree` kommt mit `0266`; die OSS-Unterarten fehlen hier wie dort, bis die
 * Messung ihren Namen in der Schnittstelle belegt (`LX-messung`).
 */
export const LEXWARE_STEUERARTEN = [
    'net',
    'gross',
    'vatfree',
    'intraCommunitySupply',
    'constructionService13b',
    'externalService13b',
    'thirdPartyCountryService',
    'thirdPartyCountryDelivery',
    'photovoltaicEquipment',
];
/** Eine Steuerart von Lexware Office. */
export const lexwareSteuerartSchema = z.enum(LEXWARE_STEUERARTEN);
// --- Der Auftrag der Übernahme aus der Oberfläche (Teilstufe W) -------------
/**
 * Die Schritte, die ein Auftrag (`vorsystem_auftrag`, Migration `0315`)
 * tragen kann: die Schritte des Läufers (`SCHRITTE` in
 * `apps/api/src/vorsystem/vorsystem-lauf.ts`) **ohne `umsatz`** (`D-75`) —
 * Schritt 3 der Einrichtung schreibt den Umsatz des Vorsystems schon über
 * denselben Dienstweg; ein zweiter Knopf wäre eine zweite Eingabe derselben
 * Zahl. Spiegel der Bedingung `vorsystem_auftrag_schritt_bekannt`; ein Test
 * hält die Liste gegen `SCHRITTE` (`[E-1910]`).
 *
 * `zaehler` (`D-96`) steht hinter `abrufen`: Er hebt die Zähler der Kunden- und Lieferantennummern auf die höchste
 * Nummer im abgerufenen Bestand — ohne etwas zu übernehmen, ohne Tor (er hebt nur an), und erst nach dem
 * Abschluss der Einrichtung erlaubt (er schreibt einen Zähler).
 *
 * `sync` (`D-79`, Spec 20.17) steht hinter `abrufen`: Er fährt einen Abruf
 * und übernimmt danach die Belege vor dem Stichtag aus dem letzten
 * abgeschlossenen Abruf — nur solange kein Stand eingefroren ist.
 *
 * `buchen` (`D-88`, Spec 21.10 Nr. 1) steht hinter `eingang`: Er bucht die
 * Belege der gebuchten Klasse (Belegdatum ab dem Beginn der Buchführung bis
 * einschliesslich zum Ende des Vorsystems) aus dem letzten abgeschlossenen
 * Abruf — erst nach dem Abschluss der Einrichtung, mit Tor.
 */
export const VORSYSTEM_AUFTRAG_SCHRITTE = [
    'pruefen',
    'abrufen',
    'zaehler',
    'sync',
    'schnappschuss',
    'stamm',
    'guthaben',
    'anschluss',
    'offen',
    'belege',
    'eingang',
    'buchen',
    'eroeffnen',
    'nachweis',
];
export const vorsystemAuftragSchrittSchema = z.enum(VORSYSTEM_AUFTRAG_SCHRITTE);
/** `verbindung` (eine lesende Anfrage, Spec 20.5) oder ein Schritt des Läufers. */
export const vorsystemAuftragArtSchema = z.enum(['verbindung', 'schritt']);
/** Die Zustände eines Auftrags, nur vorwärts (Trigger `vorsystem_auftrag_zustand`). */
export const vorsystemAuftragZustandSchema = z.enum(['angefordert', 'laeuft', 'abgeschlossen', 'abgebrochen']);
/** Warum ein Auftrag abgebrochen ist (Spec 20.6, 20.10). */
export const vorsystemAuftragFehlerartSchema = z.enum([
    'abbruch_angefordert',
    'prozess_beendet',
    'zeitueberschreitung',
    'verweigert',
    'fehler',
]);
/**
 * Was bei **offener** Einrichtung laufen darf (`D-64`): neben der
 * Verbindungsprüfung (Art `verbindung`) die drei Schritte, die keine
 * Fachdaten schreiben. Jeder andere Auftrag entsteht erst nach dem Abschluss
 * (`409 VORSYSTEM_SCHRITT_NACH_EINRICHTUNG`).
 */
export const VORSYSTEM_AUFTRAG_SCHRITTE_BEI_OFFENER_EINRICHTUNG = [
    'abrufen',
    'pruefen',
    'anschluss',
];
/**
 * Die Schalter eines Auftrags, die in `vorsystem_auftrag.eingabe` stehen
 * dürfen — nie ein Geheimnis, nie eine Personenangabe (Bedingung
 * `vorsystem_auftrag_eingabe_schluessel`). Welche zu welchem Schritt gehören,
 * hält {@link vorsystemAuftragAnlageSchema}; Stapel, Exporte, `--umstellung`
 * und `--exporte-frisch-bestaetigt` kennt der Auftrag nicht (20.11).
 */
export const vorsystemAuftragEingabeSchema = z
    .object({
    teilstufe: z.enum(['A0', 'A1']).optional(),
    bestaetigtSkr03: z.boolean().optional(),
    bestaetigtAbgleich: z.boolean().optional(),
    abgleichGezaehlt: z.number().int().min(0).optional(),
    abrufart: z.enum(['voll', 'delta']).optional(),
    letzterStand: z.boolean().optional(),
    /** Der Betreiber bestätigt: Lexware Office ist seit dem Stichtag unberührt (nur Schritt `schnappschuss`, `D-78`). */
    lexwareSeitStichtagUnberuehrt: z.boolean().optional(),
})
    .strict();
/** Welche Schalter je Schritt erlaubt sind — derselbe Zuschnitt wie `SCHRITT_SCHALTER` im Läufer. */
const SCHALTER_JE_SCHRITT = {
    pruefen: ['teilstufe', 'bestaetigtSkr03', 'bestaetigtAbgleich', 'abgleichGezaehlt'],
    abrufen: ['abrufart'],
    zaehler: [],
    sync: [],
    schnappschuss: ['letzterStand', 'lexwareSeitStichtagUnberuehrt'],
    stamm: [],
    guthaben: [],
    anschluss: [],
    offen: [],
    belege: [],
    eingang: [],
    buchen: [],
    eroeffnen: [],
    nachweis: [],
};
/**
 * Der Rumpf einer Anlage (`VorsystemAuftragService.anlegen()`). `verbindung`
 * trägt keinen Schritt, keine Schalter und schreibt nie; `pruefen` und
 * `nachweis` haben keinen echten Lauf; `guthaben` braucht die Stichtagsdatei
 * und entsteht über `guthabenAnlegen()`, nicht hier.
 */
export const vorsystemAuftragAnlageSchema = z
    .object({
    art: vorsystemAuftragArtSchema,
    schritt: vorsystemAuftragSchrittSchema.optional(),
    schreiben: z.boolean().default(false),
    eingabe: vorsystemAuftragEingabeSchema.default({}),
})
    .strict()
    .superRefine((a, ctx) => {
    if (a.art === 'verbindung') {
        if (a.schritt !== undefined) {
            ctx.addIssue({ code: 'custom', path: ['schritt'], message: 'Die Verbindungsprüfung trägt keinen Schritt.' });
        }
        if (a.schreiben) {
            ctx.addIssue({ code: 'custom', path: ['schreiben'], message: 'Die Verbindungsprüfung schreibt nichts.' });
        }
        if (Object.keys(a.eingabe).length > 0) {
            ctx.addIssue({ code: 'custom', path: ['eingabe'], message: 'Die Verbindungsprüfung kennt keine Schalter.' });
        }
        return;
    }
    if (a.schritt === undefined) {
        ctx.addIssue({ code: 'custom', path: ['schritt'], message: 'Ein Auftrag der Art schritt nennt seinen Schritt.' });
        return;
    }
    if (a.schritt === 'guthaben') {
        ctx.addIssue({
            code: 'custom',
            path: ['schritt'],
            message: 'Der Schritt guthaben braucht die Stichtagsdatei und wird mit ihr hochgeladen.',
        });
    }
    if (a.schreiben && (a.schritt === 'pruefen' || a.schritt === 'nachweis')) {
        ctx.addIssue({
            code: 'custom',
            path: ['schreiben'],
            message: `Der Schritt ${a.schritt} kennt keinen echten Lauf.`,
        });
    }
    const erlaubt = SCHALTER_JE_SCHRITT[a.schritt];
    for (const schluessel of Object.keys(a.eingabe)) {
        if (!erlaubt.includes(schluessel)) {
            ctx.addIssue({
                code: 'custom',
                path: ['eingabe', schluessel],
                message: `Der Schritt ${a.schritt} kennt den Schalter ${schluessel} nicht.`,
            });
        }
    }
});
/**
 * Die Anlage eines Trockenlaufs `guthaben` mit der Stichtagsdatei
 * (`guthabenAnlegen()`, Spec 20.9): Die Datei kommt als Bytes und nicht in
 * diesem Rumpf, der deshalb leer ist; der echte Lauf entsteht nur über
 * „Übernehmen" mit denselben Bytes.
 */
export const vorsystemAuftragGuthabenSchema = z.object({}).strict();
/**
 * Der Zwischenstand eines laufenden (und beendeten) Auftrags, wie die Schiene ihn
 * in `vorsystem_auftrag.fortschritt` ablegt — Spec 20.8, Fall `E-1992`: die
 * Zählungen des Abrufs (`AbrufFortschritt`: Kontakte, Belege, Dateien — ganze
 * Stückzahlen, kein Geld) und die letzten Zählzeilen des Läufers.
 *
 * **Nur Zählungen und Zählzeilen — nie ein Wert aus den Rohdaten** (`E-1916`):
 * kein Name, keine Anschrift, kein Betrag. Das ist die Zusage der Schiene
 * (`Fortschreiber.inhalt()`) und steht seit `E-1992` auch im Vertrag, damit die
 * Oberfläche sie lesen kann, ohne ein unbekanntes Feld zu raten. `.passthrough()`
 * bleibt: Ein Auftrag aus einer älteren Fassung mit anderer Form darf die Liste
 * nicht zerreissen — die Oberfläche liest aus dem Objekt, was sie versteht.
 */
export const vorsystemZaehlungSchema = z.number().int().nonnegative();
export const vorsystemFortschrittSchema = z
    .object({
    kontakte: vorsystemZaehlungSchema.optional(),
    belege: vorsystemZaehlungSchema.optional(),
    dateien: vorsystemZaehlungSchema.optional(),
    zeilen: z.array(z.string()).optional(),
})
    .passthrough();
/**
 * Ein Auftrag, wie die API ihn ausgibt — **ohne die Datei**, nie mit Bytes
 * (`E-1918`); ob eine steht, sagt `dateiVorhanden`.
 */
export const vorsystemAuftragSchema = z
    .object({
    id: z.string().uuid(),
    quelle: vorsystemQuelleSchema,
    art: vorsystemAuftragArtSchema,
    schritt: vorsystemAuftragSchrittSchema.nullable(),
    schreiben: z.boolean(),
    eingabe: vorsystemAuftragEingabeSchema,
    dateiVorhanden: z.boolean(),
    zustand: vorsystemAuftragZustandSchema,
    fortschritt: vorsystemFortschrittSchema.nullable(),
    ergebnis: z.record(z.string(), z.unknown()).nullable(),
    fehlerart: vorsystemAuftragFehlerartSchema.nullable(),
    fehlertext: z.string().nullable(),
    angefordertVon: z.string().uuid(),
    angelegtAm: z.string(),
    beginnAm: z.string().nullable(),
    endeAm: z.string().nullable(),
    abbruchAngefordertAm: z.string().nullable(),
})
    .strict();
/** Die Liste der Aufträge, jüngste zuerst. */
export const vorsystemAuftragListeSchema = z.object({ auftraege: z.array(vorsystemAuftragSchema) }).strict();
/* ------------------------------------------------------------------------ */
/* Die Routen der Übernahme aus der Oberfläche (Teilstufe W, Spec 20.8)      */
/* ------------------------------------------------------------------------ */
/**
 * Der Rumpf von `PUT /api/v1/vorsystem/schluessel` — der Schnittstellenschlüssel
 * des Kunden (`D-68`, `E-1914`).
 *
 * **Die Meldungen nennen die Regel und nie den Wert.** Der Schlüssel ist ein
 * Geheimnis; ein Formfehler, der ihn zitierte, brächte ihn in die Fehlerzeile
 * des Servers und in die Antwort. Die Form im Einzelnen (druckbare Zeichen ohne
 * Leerraum) prüft der Dienst beim Verschlüsseln.
 */
export const vorsystemSchluesselEingabeSchema = z
    .object({
    apiSchluessel: z
        .string()
        .min(1, 'Der Schnittstellenschlüssel darf nicht leer sein.')
        .max(4096, 'Der Schnittstellenschlüssel ist zu lang.'),
})
    .strict();
/**
 * Was die API über den Schnittstellenschlüssel sagt: **ob** er hinterlegt ist,
 * seit wann und bis wann — nie sein Wert, nie seine Länge, nie ein Ausschnitt.
 */
export const vorsystemSchluesselStatusSchema = z
    .object({
    hinterlegt: z.boolean(),
    gesetztAm: z.string().nullable(),
    geloeschtAm: z.string().nullable(),
    /** `JJJJ-MM-TT`: der Tag, an dem der Schlüssel spätestens gelöscht ist; `null`, solange kein Stand eingefroren ist. */
    fristBis: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
})
    .strict();
/** Warum ein Schritt nicht übernommen werden darf — dieselben Gründe wie das Tor des Läufers (`E-1607`). */
export const vorsystemUebernehmenGrundSchema = z.enum([
    'kein_trockenlauf',
    'trockenlauf_blockiert',
    'datei_fehlt',
    'kein_echter_lauf',
]);
/** Der Stand eines Schritts auf der Seite „Übernahme". */
export const vorsystemUebernahmeSchrittSchema = z
    .object({
    schritt: vorsystemAuftragSchrittSchema,
    /** Der jüngste Lauf dieses Schritts aus dem Protokoll des Läufers, gleich ob Oberfläche oder Kommandozeile ihn fuhr. */
    juengsterLauf: z
        .object({
        trockenlauf: z.boolean(),
        ergebnis: z.enum(['abgeschlossen', 'abgebrochen']),
        beendetAm: z.string().nullable(),
    })
        .strict()
        .nullable(),
    /** Hat der echte Lauf dieses Schritts ein Tor (`E-1607`)? `false` bei `abrufen`, `pruefen`, `nachweis`. */
    mitTor: z.boolean(),
    /** Lässt das Tor jetzt einen echten Lauf durch — gelesen aus derselben Auskunft wie das Tor selbst, nie nachgerechnet. */
    uebernehmenErlaubt: z.boolean(),
    /** Der Trockenlauf, den „Übernehmen" fortsetzt (`POST …/auftraege/:id/uebernehmen`); sonst `null`. */
    trockenlaufAuftragId: z.string().uuid().nullable(),
    grund: vorsystemUebernehmenGrundSchema.nullable(),
})
    .strict();
/**
 * Die Zählung eines Syncs je Belegart (`D-79`, Spec 20.17 Punkt 6) — die vier
 * Ergebnisse je Beleg: neu angelegt, Status nachgezogen, unverändert, mit
 * Befund. Die Belegart ist die des Vorsystems (`voucherType`, etwa
 * `invoice`, `purchaseinvoice`); nur ganze Stückzahlen, kein Betrag.
 */
export const vorsystemSyncZaehlungSchema = z
    .object({
    belegart: z.string().regex(/^[a-z][a-z0-9_]{0,40}$/),
    neu: vorsystemZaehlungSchema,
    status: vorsystemZaehlungSchema,
    unveraendert: vorsystemZaehlungSchema,
    befund: vorsystemZaehlungSchema,
})
    .strict();
/** Was die Seite über den Sync weiss: ob er jetzt angeboten wird, und der letzte abgeschlossene. */
export const vorsystemSyncStandSchema = z
    .object({
    /** Nach dem ersten abgeschlossenen Abruf und solange kein Stand eingefroren ist (Spec 20.17 Punkt 5). */
    moeglich: z.boolean(),
    letzter: z
        .object({
        am: z.string(),
        zaehlung: z.array(vorsystemSyncZaehlungSchema),
        /**
         * Schon übernommene Rechnungen, die in diesem Sync ihre Einzelpositionen statt einer Zeile je
         * Steuersatzgruppe bekommen haben (`D-85`); 0 bei einem Sync, der die Zahl noch nicht führte.
         */
        positionenNachgezogen: vorsystemZaehlungSchema,
    })
        .strict()
        .nullable(),
})
    .strict();
/** Die zwei Wege zum Stand (Spec 6.2 Punkt 3), seit `D-80` am Abruf gemessen. */
export const vorsystemSchnappschussWegSchema = z.enum(['weg1_am_stichtag', 'weg2_datierte_zahlungen']);
/**
 * Eine Buchungskategorie aus Lexware Office ohne Sachkonto — eine Zeile der
 * Arbeitsliste auf der Seite (`D-81` Punkt 7, `E-2057`). Name und Typ aus dem
 * Katalog `/v1/posting-categories`; ein Konto gibt es für sie noch keines.
 */
export const vorsystemOffeneKategorieSchema = z
    .object({
    kategorie: z.string().min(1),
    name: z.string().nullable(),
    typ: z.string().nullable(),
})
    .strict();
/* ------------------------------------------------------------------------ */
/* Die Sachkonten aus Lexware Office (`D-82`, Spec 20.20)                   */
/* ------------------------------------------------------------------------ */
/** Eine Kontonummer, wie sie der Export von Lexware Office oder dieser Kontenrahmen trägt. */
const kontonummerSchema = z.string().regex(/^[0-9]{4,8}$/, 'Eine Kontonummer besteht aus vier bis acht Ziffern.');
/** Ein Konto des Exports mit der Zahl der Belege, die es dort tragen. */
export const vorsystemKontoStimmeSchema = z
    .object({
    konto: kontonummerSchema,
    belege: z.number().int().nonnegative(),
})
    .strict();
/**
 * Woher die Empfehlung für das Sachkonto einer Kategorie kommt (`D-93`), in der Reihenfolge ihres Rangs — die erste
 * Quelle mit Ergebnis gewinnt:
 *
 * - `katalog`: Lexware Office selbst — das Konto, das der Katalog `/v1/posting-categories` für die Kategorie trägt,
 *   sonst die Zuordnung, die Lexware Office in seiner öffentlichen Hilfe nennt (`lexware-kategorien-skr03.json`).
 *   Das Konto aus dem Katalog der Schnittstelle steht vor allen anderen; die Zuordnung aus der Hilfe nach dem Verlauf.
 * - `export`: die Belege dieser Kategorie im DATEV-Export aus Lexware Office.
 * - `verlauf`: auf welches Konto die Eingangsrechnungen der Lieferanten dieser Kategorie hier bisher kontiert sind.
 * - `name`: Namensgleichheit mit einem Konto dieses Kontenrahmens oder einem Eintrag der Synonymliste.
 */
export const vorsystemEmpfehlungQuellen = ['katalog', 'export', 'verlauf', 'name'];
/** Wie sicher die Empfehlung ist (`D-93`): `hoch`, `mittel`, `niedrig`. */
export const vorsystemEmpfehlungSicherheiten = ['hoch', 'mittel', 'niedrig'];
/**
 * Die eine Empfehlung für das Sachkonto einer Kategorie ohne bestätigtes Konto (`D-93`, `E-2084`). Nie eine
 * Abbildung: wirksam wird erst die Bestätigung.
 *
 * - `begruendung`: ein Satz für den Kunden, woher die Empfehlung kommt.
 * - `warnung`: eine Plausibilitätsprüfung hat die Sicherheit auf höchstens `mittel` gesenkt (Art der Kategorie passt
 *   nicht zur Kontenart, Sammel- oder Privatkonto, steuerlich sensibles Konto) — der Satz dazu, sonst `null`.
 * - `imKontenrahmen`: Führt dieser Kontenrahmen das Konto heute? Ohne es lässt sich die Empfehlung nicht bestätigen,
 *   nur das Konto anlegen.
 * - `sicher`: `hoch`, ohne Warnung und im Kontenrahmen — nur solche bestätigt „sichere Vorschläge bestätigen“.
 */
export const vorsystemKontoEmpfehlungSchema = z
    .object({
    konto: kontonummerSchema,
    quelle: z.enum(vorsystemEmpfehlungQuellen),
    sicherheit: z.enum(vorsystemEmpfehlungSicherheiten),
    begruendung: z.string().min(1),
    warnung: z.string().min(1).nullable(),
    imKontenrahmen: z.boolean(),
    sicher: z.boolean(),
})
    .strict();
/**
 * Die Zählung der Seite, am Server gerechnet (`D-93`): `gesamt` Kategorien; `bestaetigt`; `ausLexware` (es gilt ein
 * Konto aus dem Katalog von Lexware Office, unbestätigt); `offen` (kein Konto gilt), davon `mitVorschlag` und
 * `ohneVorschlag`; `sicher` (unbestätigte Kategorien mit sicherer Empfehlung); `belegeUngeklaert` — übernommene
 * Belege mit mindestens einer Position, deren Sachkonto „[ungeklärt]“ ist.
 */
export const vorsystemKontenZaehlungSchema = z
    .object({
    gesamt: z.number().int().nonnegative(),
    bestaetigt: z.number().int().nonnegative(),
    ausLexware: z.number().int().nonnegative(),
    offen: z.number().int().nonnegative(),
    mitVorschlag: z.number().int().nonnegative(),
    ohneVorschlag: z.number().int().nonnegative(),
    sicher: z.number().int().nonnegative(),
    belegeUngeklaert: z.number().int().nonnegative(),
})
    .strict();
/** Ein häufig verwendetes Konto dieses Betriebs (die ersten fünf), für die Kontoauswahl. */
export const vorsystemHaeufigesKontoSchema = z
    .object({
    konto: kontonummerSchema,
    name: z.string(),
    verwendungen: z.number().int().nonnegative(),
})
    .strict();
/**
 * Eine Buchungskategorie aus Lexware Office und ihr Sachkonto (`D-82` Punkt 1a).
 *
 * - `konto`: das Sachkonto, das **gilt** — bestätigt, oder aus dem Katalog von Lexware Office (`D-81` Punkt 7);
 *   `null` heisst offen, die Positionen stehen als „[ungeklärt]“.
 * - `vorschlag`: das eine Konto, auf das alle eindeutigen Belege dieser Kategorie im DATEV-Export gingen, nach der
 *   Abbildung Konto → Konto; `null` ohne Export oder bei Konten in Konkurrenz.
 * - `konkurrenz`: die Konten des Exports mit ihrer Belegzahl (bei einem Vorschlag genau eines), übersetzt.
 * - `vorbelegung`: was das Auswahlfeld zeigt — das geltende Konto, sonst der Vorschlag, wenn dieser Kontenrahmen ihn
 *   heute führt, sonst nichts. Der Server entscheidet; die Seite rechnet nicht nach. Seit `D-93`: das geltende Konto,
 *   sonst die Empfehlung, wenn sie im Kontenrahmen steht und nicht `niedrig` ist und nicht aus der blossen
 *   Namensgleichheit kommt.
 * - `belegzahl`, `summe`: die übernommenen Belege mit dieser Kategorie und die Bruttosumme ihrer Positionen dieser
 *   Kategorie (`vorsystem_beleg_position`); `belege` dagegen zählt die Belege des DATEV-Exports.
 * - `empfehlung`: die eine Empfehlung (`D-93`) für eine Kategorie ohne Bestätigung, sonst `null`.
 * - `bestaetigtAus`: `vorschlag` (die Empfehlung übernommen) oder `manuell` (ein Konto gewählt); `bestaetigtVon` die
 *   Kennung der Person.
 */
export const vorsystemKategorieAbbildungSchema = z
    .object({
    kategorie: z.string().min(1),
    name: z.string().nullable(),
    typ: z.string().nullable(),
    konto: kontonummerSchema.nullable(),
    herkunft: z.enum(['stapel', 'katalog']),
    bestaetigt: z.boolean(),
    bestaetigtAm: z.string().nullable(),
    belege: z.number().int().nonnegative(),
    vorschlag: kontonummerSchema.nullable(),
    konkurrenz: z.array(vorsystemKontoStimmeSchema),
    vorbelegung: kontonummerSchema.nullable(),
    belegzahl: z.number().int().nonnegative(),
    summe: centAmount,
    empfehlung: vorsystemKontoEmpfehlungSchema.nullable(),
    bestaetigtAus: z.enum(['vorschlag', 'manuell']).nullable(),
    bestaetigtVon: z.string().uuid().nullable(),
})
    .strict();
/**
 * Ein Konto des DATEV-Exports, das dieser Kontenrahmen heute nicht führt, und das Konto dieses Systems dafür
 * (`D-82` Punkt 1b). `konto` gilt nur mit `bestaetigt`; ohne Bestätigung ist es leer.
 */
export const vorsystemExportKontoAbbildungSchema = z
    .object({
    kontoVorsystem: kontonummerSchema,
    belege: z.number().int().nonnegative(),
    konto: kontonummerSchema.nullable(),
    bestaetigt: z.boolean(),
    bestaetigtAm: z.string().nullable(),
})
    .strict();
/** Ein Konto dieses Kontenrahmens, das heute gilt — eine Zeile des Auswahlfelds. */
export const vorsystemKontenrahmenKontoSchema = z
    .object({
    konto: kontonummerSchema,
    name: z.string(),
})
    .strict();
/**
 * Die Auskunft der Seite „Sachkonten aus Lexware Office“ (`GET /api/v1/vorsystem/kontenabbildung`, `D-82`).
 * `exportGelesen` sagt, ob je ein DATEV-Export für die Übernahme gelesen wurde — ohne ihn gibt es keinen Vorschlag
 * aus dem Export. `zaehlung` rechnet der Server (`D-93`); `haeufigeKonten` sind die fünf Konten, die dieser Betrieb
 * am häufigsten für Kategorien und Eingangsrechnungen verwendet.
 */
export const vorsystemKontenabbildungSchema = z
    .object({
    angelegt: z.boolean(),
    exportGelesen: z.boolean(),
    kategorien: z.array(vorsystemKategorieAbbildungSchema),
    exportKonten: z.array(vorsystemExportKontoAbbildungSchema),
    kontenrahmen: z.array(vorsystemKontenrahmenKontoSchema),
    zaehlung: vorsystemKontenZaehlungSchema,
    haeufigeKonten: z.array(vorsystemHaeufigesKontoSchema),
})
    .strict();
/* ------------------------------------------------------------------------ */
/* Zuordnen in Stapeln (`D-93`, `E-2085`)                                    */
/* ------------------------------------------------------------------------ */
/** Höchstens so viele Kategorien nimmt ein Stapel auf einmal (die Seite schickt alle markierten in einem Aufruf). */
export const VORSYSTEM_STAPEL_MAX = 1000;
const kategorieListeSchema = z
    .array(z.string().regex(/^[A-Za-z0-9_-]{1,100}$/, 'Eine Kategorie-Kennung aus Buchstaben, Ziffern, - und _.'))
    .min(1, 'Bitte mindestens eine Kategorie wählen.')
    .max(VORSYSTEM_STAPEL_MAX);
/**
 * `POST …/kontenabbildung/kategorien/bestaetigen` — übernimmt für jede genannte Kategorie ihre Empfehlung.
 * `regelMerken`: je Lieferant der Kategorie ohne eigenes Standardkonto wird das Konto als Standardkonto eingetragen
 * (bestehende nie überschrieben).
 */
export const vorsystemKategorienBestaetigenSchema = z
    .object({
    kategorien: kategorieListeSchema,
    regelMerken: z.boolean({ required_error: 'Bitte angeben, ob die Zuordnung als Regel gemerkt wird.' }),
})
    .strict();
/** `POST …/kontenabbildung/kategorien/zuordnen` — ein Konto für alle genannten Kategorien, bestätigt. */
export const vorsystemKategorienZuordnenSchema = z
    .object({
    kategorien: kategorieListeSchema,
    konto: z
        .string({ required_error: 'Bitte ein Konto wählen.', invalid_type_error: 'Bitte ein Konto wählen.' })
        .regex(/^[0-9]{4,8}$/, 'Bitte ein Konto aus der Liste wählen.'),
    regelMerken: z.boolean({ required_error: 'Bitte angeben, ob die Zuordnung als Regel gemerkt wird.' }),
})
    .strict();
/** `POST …/kontenabbildung/kategorien/sichere-bestaetigen` — bestätigt jede sichere Empfehlung. */
export const vorsystemSichereBestaetigenSchema = z
    .object({
    regelMerken: z.boolean({ required_error: 'Bitte angeben, ob die Zuordnung als Regel gemerkt wird.' }),
})
    .strict();
/**
 * Die Antwort jedes Stapels (`bestaetigen`, `zuordnen`, `sichere-bestaetigen`, `rueckgaengig`): `stapelId` zum
 * Rückgängigmachen; `aktualisiert` — die geänderten Kategorien in ihrer neuen Form; `uebersprungen` — genannte
 * Kategorien, die nichts zu übernehmen hatten (keine Empfehlung im Kontenrahmen, beim Rückgängigmachen seither
 * anders geändert); `regelnAngelegt` — eingetragene Standardkonten von Lieferanten; `zaehlung` danach.
 */
export const vorsystemKontenStapelErgebnisSchema = z
    .object({
    stapelId: z.string().uuid(),
    aktualisiert: z.array(vorsystemKategorieAbbildungSchema),
    uebersprungen: z.array(z.string()),
    regelnAngelegt: z.number().int().nonnegative(),
    zaehlung: vorsystemKontenZaehlungSchema,
})
    .strict();
/** Eine Gegenpartei der übernommenen Belege einer Kategorie, mit der Zahl ihrer Belege. */
export const vorsystemKategorieGegenparteiSchema = z
    .object({
    name: z.string(),
    art: z.enum(['kunde', 'lieferant']),
    belege: z.number().int().positive(),
})
    .strict();
/** Ein Beispielbeleg einer Kategorie: Datum, Gegenpartei, Betrag der Positionen dieser Kategorie (brutto). */
export const vorsystemKategorieBeispielSchema = z
    .object({
    datum: isoDate.nullable(),
    belegnummer: z.string().nullable(),
    gegenpartei: z.string().nullable(),
    betrag: centAmount,
})
    .strict();
/**
 * `GET …/kontenabbildung/kategorien/:kategorie` — die Einzelheiten einer Kategorie für das Detailfeld: die häufigsten
 * Gegenparteien (höchstens fünf) und drei Beispielbelege (die jüngsten).
 */
export const vorsystemKategorieDetailsSchema = z
    .object({
    kategorie: z.string().min(1),
    gegenparteien: z.array(vorsystemKategorieGegenparteiSchema),
    beispiele: z.array(vorsystemKategorieBeispielSchema),
})
    .strict();
/**
 * Die Antwort auf `POST /api/v1/vorsystem/kontenabbildung/export` (`D-93`, `E-2085`): was aus dem hochgeladenen
 * DATEV-Export gelesen wurde (nur Zählungen — die Datei selbst bleibt nicht liegen) und die Auskunft danach.
 */
export const vorsystemKontenExportErgebnisSchema = z
    .object({
    konten: z.number().int().nonnegative(),
    fremdeKonten: z.number().int().nonnegative(),
    mehrdeutig: z.number().int().nonnegative(),
    kategorienNeu: z.number().int().nonnegative(),
    kategorienAktualisiert: z.number().int().nonnegative(),
    auskunft: vorsystemKontenabbildungSchema,
})
    .strict();
/** Der Rumpf von „Bestätigen“: das gewählte Konto dieses Systems (`PUT …/kontenabbildung/kategorien/:kategorie`, `…/konten/:kontoVorsystem`). */
export const vorsystemKontoWahlSchema = z
    .object({
    konto: z
        .string({ required_error: 'Bitte ein Konto wählen.', invalid_type_error: 'Bitte ein Konto wählen.' })
        .regex(/^[0-9]{4,8}$/, 'Bitte ein Konto aus der Liste wählen.'),
})
    .strict();
/**
 * Woher die Zahlungen auf Belege aus dem Vorsystem ab dem Beginn der Buchführung kommen (`D-91`, Spalte
 * `vorsystem_projekt.zahlungsquelle`, `0341`): aus dem Vorsystem (Vorgabe), aus der Bank oder aus beiden.
 */
export const VORSYSTEM_ZAHLUNGSQUELLEN = ['lexware', 'bank', 'beide'];
export const vorsystemZahlungsquelleSchema = z.enum(VORSYSTEM_ZAHLUNGSQUELLEN);
/**
 * Die zwei Einstellungen der Übernahme (`D-88`, `D-91`, Schritt „Vorsystem“ der Einrichtung):
 * bis zu welchem Tag das Vorsystem läuft (`null`: nicht eingestellt) und woher die Zahlungen kommen.
 */
export const vorsystemEinstellungSchema = z
    .object({
    vorsystemBis: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    zahlungsquelle: vorsystemZahlungsquelleSchema,
})
    .strict();
/**
 * Der Rumpf von `PUT /api/v1/vorsystem/einstellung`: beide Einstellungen zugleich. Der Tag liegt nie vor dem
 * Beginn der Buchführung und wird nach dem ersten festgehaltenen Stand nur nach hinten verlegt — das prüft der
 * Dienst gegen den Bestand, nicht dieser Vertrag.
 */
export const vorsystemEinstellungEingabeSchema = z
    .object({
    vorsystemBis: z
        .string({ required_error: 'Bitte einen Tag eintragen.', invalid_type_error: 'Bitte einen Tag eintragen.' })
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format JJJJ-MM-TT erwartet.'),
    zahlungsquelle: vorsystemZahlungsquelleSchema,
})
    .strict();
/**
 * Die Zahlen der Phase „Zahlungen aus Lexware Office" (`D-91`, Spec 21.7) — wie der jüngste Sync sie ablegt. Nur
 * ganze Stückzahlen, kein Betrag.
 */
export const vorsystemZahlungenZaehlungSchema = z
    .object({
    zahlungsquelle: vorsystemZahlungsquelleSchema.nullable(),
    /** Posten mit Datum ab dem Beginn bis zum Ende von Lexware Office. */
    posten: vorsystemZaehlungSchema,
    /** In diesem Lauf als Tilgung gebucht. */
    gebucht: vorsystemZaehlungSchema,
    /** Schon in einem früheren Lauf gebucht. */
    bereits: vorsystemZaehlungSchema,
    /** Vor dem Beginn — Teil des Stands. */
    vorBeginn: vorsystemZaehlungSchema,
    /** Der Beleg ist hier noch nicht gebucht; die Zahlung wartet. */
    wartet: vorsystemZaehlungSchema,
    /** Mit Befund (Liste der Befunde). */
    befund: vorsystemZaehlungSchema,
    /** Unter der Zahlungsquelle `bank`: Posten im Zeitraum, die nicht übernommen werden. */
    nichtUebernommen: vorsystemZaehlungSchema,
    /** Unter `beide`: Bewegungen der Bank, die selbsttätig an den Geldtransit gebucht wurden. */
    bankBestaetigt: vorsystemZaehlungSchema,
})
    .strict();
/**
 * Die Zahlen des Schritts «Belege ab dem Beginn buchen» (`daten` im Ergebnis des Laufs, `vorsystem-lauf.ts`,
 * `schrittBuchen()`; `LXB-nachweis`, `E-2112`): was die Seite je Lauf zeigt — Belege im Zeitraum, die Eingangsrechnungen
 * (gebucht, angehalten, noch nicht übernommen) und die Zahlungen. Nur ganze Stückzahlen, kein Betrag. Gelesen aus dem Ergebnis
 * des jüngsten Laufs (`VorsystemAuftrag.ergebnis`): ein Ergebnis, das diese Form nicht erfüllt, ist kein Fehler der Seite,
 * sondern ein Lauf ohne diese Zahlen (eine ältere Fassung, ein Abbruch vor dem Zählen).
 */
export const vorsystemBuchenDatenSchema = z.object({
    stichtag: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    vorsystemBis: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /** Belege der gebuchten Klasse im Abruf (Ausgangsbelege und Eingangsrechnungen), nach dem Ende und noch nicht buchbare. */
    buchen: z.object({
        gebucht: vorsystemZaehlungSchema,
        nachEnde: vorsystemZaehlungSchema,
        nochNichtBuchbar: vorsystemZaehlungSchema,
    }),
    /** Die Eingangsseite: im Zeitraum, gebucht, im Lauf nachgebucht, buchbar (Prüflauf), angehalten, noch nicht übernommen. */
    eingang: z.object({
        belege: vorsystemZaehlungSchema,
        bereitsGebucht: vorsystemZaehlungSchema,
        nachgebucht: vorsystemZaehlungSchema,
        buchbar: vorsystemZaehlungSchema,
        angehalten: vorsystemZaehlungSchema,
        nichtUebernommen: vorsystemZaehlungSchema,
    }),
    zahlungen: vorsystemZahlungenZaehlungSchema,
});
/**
 * Eine Gegenüberstellung des eigenen Nachweises (`daten.gegenueberstellungen` im Ergebnis des Schritts `nachweis`,
 * `vorsystem-lauf.ts`, `schrittNachweis()`; `D-101`, `E-2159`): links der Stand, rechts das Übernommene, die dritte
 * Grösse „mit Befund“ und die benannten Posten, je mit Anzahl und — wo die Zeile Summen führt — Summe. `schritt` nennt
 * den Schritt, dessen Arbeit die Zeile zählt; die Seite nennt ihn mit seinem Titel. Die Handlung im Wortlaut des
 * Läufers (`handlung`) gehört nicht zum Vertrag: Die Seite hat ihren eigenen.
 */
export const vorsystemNachweisZeileSchema = z.object({
    schluessel: z.string().min(1),
    bezeichnung: z.string().min(1),
    schritt: vorsystemAuftragSchrittSchema,
    links: z.string(),
    rechts: z.string(),
    anzahlLinks: vorsystemZaehlungSchema,
    anzahlRechts: vorsystemZaehlungSchema,
    summeLinks: centAmount.nullable(),
    summeRechts: centAmount.nullable(),
    mitBefund: z.object({
        anzahl: vorsystemZaehlungSchema,
        summe: centAmount.nullable(),
        arten: z.array(z.object({ art: z.string().min(1), anzahl: vorsystemZaehlungSchema })),
    }),
    posten: z.array(z.object({
        schluessel: z.string().min(1),
        bezeichnung: z.string().min(1),
        seite: z.enum(['links', 'rechts']),
        anzahl: vorsystemZaehlungSchema,
        summe: centAmount.nullable(),
    })),
    gleich: z.boolean(),
});
/**
 * Die Zahlen des Schritts «Nachweis» (`daten` im Ergebnis des Laufs; `D-101`, `E-2159`): Stichtag, Zahl der Belege im
 * Stand und die Gegenüberstellungen. Gelesen aus dem Ergebnis des jüngsten Laufs (`VorsystemAuftrag.ergebnis`): Ein
 * Ergebnis, das diese Form nicht erfüllt — eine ältere Fassung ohne „mit Befund“, ein Abbruch vor dem Zählen —, ist
 * kein Fehler der Seite, sondern ein Lauf ohne diese Zahlen.
 */
export const vorsystemNachweisDatenSchema = z.object({
    stichtag: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    belegeImStand: vorsystemZaehlungSchema,
    gegenueberstellungen: z.array(vorsystemNachweisZeileSchema),
});
/**
 * Ein offener Transitposten (`vorsystem_transit_offen`, `0344`): Geld, das Lexware Office als bezahlt führt und
 * dessen Bewegung auf dem Kontoauszug hier noch nicht kontiert ist — oder umgekehrt. Arbeit, kein Fehler.
 */
export const vorsystemTransitPostenSchema = z
    .object({
    belegnummer: z.string().nullable(),
    belegart: z.string().nullable(),
    zahlungen: vorsystemZaehlungSchema,
    letzteZahlung: isoDate.nullable(),
    /** Saldo auf dem Geldtransit, aus Sicht des Kontos (Soll positiv). */
    offen: centAmount,
})
    .strict();
/** Was die Seite über die Zahlungen aus Lexware Office weiss. */
export const vorsystemZahlungenStandSchema = z
    .object({
    /** Die Zahlen des jüngsten abgeschlossenen Syncs; `null`, solange keiner sie führte. */
    letzter: vorsystemZahlungenZaehlungSchema.nullable(),
    /** Die Arbeitsliste der offenen Transitposten. */
    transitOffen: z.array(vorsystemTransitPostenSchema),
})
    .strict();
/** Der Stand der Übernahme: eine Auskunft, die die Seite und der Assistent gemeinsam lesen. */
export const vorsystemUebernahmeStatusSchema = z
    .object({
    quelle: vorsystemQuelleSchema,
    /** Gibt es die Übernahme (Zeile `vorsystem_projekt`)? Sie entsteht mit dem ersten Schlüssel oder dem ersten Schritt. */
    angelegt: z.boolean(),
    schluessel: vorsystemSchluesselStatusSchema,
    abrufe: z
        .object({
        /** Die Zahl der abgeschlossenen Abrufe. */
        anzahl: z.number().int().min(0),
        letzterAm: z.string().nullable(),
    })
        .strict(),
    stand: z
        .object({
        /** Der Beginn der Buchführung (Journalbeginn) aus der Einrichtung; `null`, solange er nicht gesetzt ist. */
        stichtag: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
        letzterStand: z
            .object({
            stichtag: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            eingefrorenAm: z.string(),
        })
            .strict()
            .nullable(),
        /**
         * Der am letzten Abruf gemessene Weg (Spec 20.18, `D-80`): `weg2_datierte_zahlungen`, wenn Lexware Office
         * jede Zahlung datiert — dann darf der Stand nach dem Stichtag ohne Bestätigung entstehen —, sonst
         * `weg1_am_stichtag`. `null`, solange kein Abruf, kein Sync und kein Schritt «Stand festhalten» aus der
         * Oberfläche ihn gemessen hat.
         */
        weg: vorsystemSchnappschussWegSchema.nullable(),
    })
        .strict(),
    offenerAuftrag: vorsystemAuftragSchema.nullable(),
    sync: vorsystemSyncStandSchema,
    schritte: z.array(vorsystemUebernahmeSchrittSchema),
    /** Die Checkliste vor dem Stichtag — dieselben Sätze, die der Schritt `pruefen` des Läufers ausgibt. */
    checkliste: z.array(z.string()),
    /**
     * Der Hinweis zum Abschluss der jüngsten Stufe, deren **echter** Lauf abgeschlossen ist — dieselben Sätze, die
     * der Läufer am Ende dieses Laufs ausgibt. Leer, solange keine Stufe abgeschlossen ist; ein Trockenlauf oder ein
     * Abbruch ändert ihn nicht.
     */
    abschlusshinweis: z.array(z.string()),
    /**
     * Die Buchungskategorien ohne Sachkonto (`E-2057`): weder bestätigt noch aus dem
     * Katalog mit Kontonummer. Ein Beleg mit einer solchen Kategorie ist übernommen,
     * sein Sachkonto steht als [ungeklärt]. `optional()`: Eine ältere Auskunft trägt sie nicht.
     */
    kategorienOffen: z.array(vorsystemOffeneKategorieSchema).optional(),
    /**
     * Die zwei Einstellungen der Übernahme (`D-88`, `D-91`). `optional()`: Eine ältere Auskunft trägt sie nicht;
     * ohne Übernahme steht der Tag auf `null` und die Zahlungsquelle auf der Vorgabe.
     */
    einstellung: vorsystemEinstellungSchema.optional(),
    /**
     * Die Zahlungen aus Lexware Office (`D-91`): die Zahlen des jüngsten Syncs und die offenen Transitposten.
     * `optional()`: Eine ältere Auskunft trägt sie nicht.
     */
    zahlungen: vorsystemZahlungenStandSchema.optional(),
})
    .strict();
/**
 * Das eine Bit, das die Menüspalte braucht (`GET /api/v1/vorsystem/uebernahme/angelegt`, `E-2002`): Gibt es die
 * Übernahme (Zeile `vorsystem_projekt`)? Dieselbe Auskunft wie `angelegt` im Stand, aber mit einer einzigen
 * Abfrage statt 16 (gemessen) — die Schale fragt sie bei jedem Aufbau, der Stand nur die Seite selbst.
 */
export const vorsystemAngelegtSchema = z
    .object({
    angelegt: z.boolean(),
})
    .strict();
/**
 * Ein Befund, wie die Seite ihn zeigt: Art, Beschreibung der Regel, Handlung,
 * der Beleg im Vorsystem und seine Kennung dort — **nie ein Name, eine
 * Anschrift oder ein Betrag** (`E-1916`, `E-1608` über HTTP). Dieselben
 * Felder, die der Läufer druckt.
 */
export const vorsystemAnzeigeBefundSchema = z
    .object({
    art: vorsystemBefundartSchema,
    blockierend: z.boolean(),
    beschreibung: z.string().min(1),
    handlung: z.string().min(1),
    belegnummer: z.string().nullable(),
    belegdatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    fremdschluessel: z.string().nullable(),
    feld: z.string().nullable(),
})
    .strict();
/** Die Befunde des jüngsten beendeten Laufs eines Schritts. */
export const vorsystemBefundeSchrittSchema = z
    .object({
    schritt: vorsystemAuftragSchrittSchema,
    auftragId: z.string().uuid(),
    trockenlauf: z.boolean(),
    ergebnis: z.enum(['abgeschlossen', 'abgebrochen', 'verweigert']),
    endeAm: z.string().nullable(),
    /** Warum der Lauf abbrach oder verweigert wurde — der Satz des Läufers, ohne Personenangaben. */
    meldung: z.string().nullable(),
    befunde: z.array(vorsystemAnzeigeBefundSchema),
})
    .strict();
/** `GET /api/v1/vorsystem/befunde`: die Arbeitsliste, je Schritt der jüngste Lauf. */
export const vorsystemBefundeSchema = z
    .object({ schritte: z.array(vorsystemBefundeSchrittSchema) })
    .strict();
/* ------------------------------------------------------------------------ */
/* Entscheid je Kontakt, wenn seine Nummer hier schon vergeben ist (`D-97`)  */
/* ------------------------------------------------------------------------ */
/** Die Rolle eines Kontakts im Vorsystem: Kunde oder Lieferant (`0348`). */
export const VORSYSTEM_KONTAKT_ROLLEN = ['kunde', 'lieferant'];
export const vorsystemKontaktRolleSchema = z.enum(VORSYSTEM_KONTAKT_ROLLEN);
/**
 * Die zwei Wege bei einer vergebenen Nummer (`D-97`): den Kontakt mit einem bestehenden Kunden oder Lieferanten
 * verknüpfen, oder ihn unter einer freien Nummer des hiesigen Kreises anlegen.
 */
export const VORSYSTEM_KONTAKTENTSCHEID_ARTEN = ['verknuepfen', 'umnummerieren'];
export const vorsystemKontaktentscheidArtSchema = z.enum(VORSYSTEM_KONTAKTENTSCHEID_ARTEN);
/** Die Form der Kennung eines Kontakts (`vorsystem_kontakt_entscheid_kontakt_form`). */
const KONTAKT_KENNUNG = /^[A-Za-z0-9_-]{1,100}$/;
/**
 * `POST /api/v1/vorsystem/kontaktentscheide`: ein Entscheid zu einem Kontakt mit dem Befund
 * `kunde_nummer_kollidiert` bzw. `lieferant_nummer_kollidiert`. `verknuepfen` nennt `zielId`, `umnummerieren`
 * nennt `nummer`; die Begründung ist immer Pflicht (`E-2143`).
 */
export const vorsystemKontaktentscheidEingabeSchema = z
    .object({
    kontakt: z
        .string({ required_error: 'Die Kennung des Kontakts fehlt.' })
        .regex(KONTAKT_KENNUNG, 'Die Kennung des Kontakts hat nicht die erwartete Form.'),
    rolle: vorsystemKontaktRolleSchema,
    art: vorsystemKontaktentscheidArtSchema,
    zielId: z.string().uuid('Bitte einen Eintrag aus der Liste auswählen.').optional(),
    nummer: z
        .string()
        .trim()
        .regex(/^[0-9]{1,10}$/, 'Bitte eine Nummer aus Ziffern eingeben.')
        .optional(),
    begruendung: z
        .string({ required_error: 'Bitte eine Begründung eingeben.', invalid_type_error: 'Bitte eine Begründung eingeben.' })
        .trim()
        .min(1, 'Bitte eine Begründung eingeben.')
        .max(1000, 'Die Begründung ist zu lang (höchstens 1000 Zeichen).'),
})
    .strict()
    .superRefine((e, ctx) => {
    if (e.art === 'verknuepfen') {
        if (e.zielId === undefined) {
            ctx.addIssue({ code: 'custom', path: ['zielId'], message: 'Bitte einen Eintrag aus der Liste auswählen.' });
        }
        if (e.nummer !== undefined) {
            ctx.addIssue({ code: 'custom', path: ['nummer'], message: 'Beim Verknüpfen gibt es keine neue Nummer.' });
        }
        return;
    }
    if (e.nummer === undefined) {
        ctx.addIssue({ code: 'custom', path: ['nummer'], message: 'Bitte eine Nummer aus Ziffern eingeben.' });
    }
    if (e.zielId !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['zielId'], message: 'Beim Vergeben einer neuen Nummer gibt es keinen Eintrag.' });
    }
});
/** Ein getroffener Entscheid. `personenkonto` ist die Nummer, unter der der Kontakt hier geführt wird. */
export const vorsystemKontaktentscheidSchema = z
    .object({
    id: z.string().uuid(),
    kontakt: z.string(),
    rolle: vorsystemKontaktRolleSchema,
    art: vorsystemKontaktentscheidArtSchema,
    /** Die Nummer des Kontakts im Vorsystem, als der Entscheid fiel. */
    nummerVorsystem: z.string(),
    /** Der verknüpfte Kunde oder Lieferant; `null` beim Umnummerieren. */
    zielId: z.string().uuid().nullable(),
    /** Die Debitoren- bzw. Kreditorennummer hier: die des Ziels oder die neue. */
    personenkonto: z.string(),
    begruendung: z.string(),
    entschiedenVon: z.string().uuid(),
    entschiedenAm: z.string(),
    /** `true`, sobald ein echter Lauf der Stammdaten ihn angewandt hat; danach lässt er sich nicht zurücknehmen. Zurückgenommene erscheinen nicht. */
    angewandt: z.boolean(),
})
    .strict();
/** `GET /api/v1/vorsystem/kontaktentscheide`. */
export const vorsystemKontaktentscheideSchema = z
    .object({ entscheide: z.array(vorsystemKontaktentscheidSchema) })
    .strict();
//# sourceMappingURL=vorsystem.js.map